import React, { useState, useEffect, useContext } from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import BackdropMui from '@mui/material/Backdrop';

import EnhancedTable from 'components/EnhancedTable';
import ActionDialog from 'components/ActionDialog';
import ContextStore from 'modules/context';
import { firestoreListener, callFunction } from 'modules/firebase';
import LeavesView from './LeavesView';

const Backdrop = styled(BackdropMui)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  color: '#fff',
}));

const statusColor = {
  pending: '#c83cc8',
  reject: '#fe2851',
  void: '#fe2851',
  done: '#00b900',
}

function LeaveList() {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const [leaves, setLeaves] = useState([])
  const [dialogData, setDialogData] = useState(null);
  const [loading, setLoading] = useState(false)
  const { currentUser } = useContext(ContextStore)

  useEffect(() => {
    const yearStartDate = `${dayjs().format('YYYY')}-01-01`
    const unsubscribe = firestoreListener({
      collection: 'leaves',
      where: [['createdBy', '==', currentUser.key], ['status', '!=', 'void_']],
      filter: (docData) => docData.startDate >= yearStartDate,
      onData: (data) => {
        setLeaves(data)
      }
    })

    return () => unsubscribe()
  }, [currentUser.key]);


  const formatData = (leave) => {
    const newData = { ...leave }
    newData.start = `${newData.startDate} ${newData.startTime}`
    newData.typeName = formatMessage({ id: `leaveType.${newData.type}` })
    newData.statusName = `${formatMessage({ id: `leaveStatus.${newData.status}` })}`
    newData.end = `${newData.endDate} ${newData.endTime}`
    newData.day = `${newData.days}天${newData.minutes / 60}小時`
    newData.date = dayjs(newData.createdAt.seconds * 1000).format('YYYY-MM-DD HH:mm')
    newData.textColor = statusColor[newData.status]

    return newData
  }

  const headerCells = [
    { text: 'type', sort: 'type' },
    { text: 'start', sort: 'start' },
    { text: 'end', sort: 'end' },
    { text: 'days', sort: 'days' },
    { text: 'date', sort: 'date' },
    { text: 'state', sort: 'status' },
  ].map(c => { c.text = formatMessage({ id: `leaves.table.${c.text}` }); return c })

  const rowCells = [
    { field: 'typeName' },
    { field: 'start' },
    { field: 'end' },
    { field: 'day' },
    { field: 'date' },
    { field: 'statusName', textColor: true },
  ]

  async function handleExecute(data) {
    const { text } = data
    const { filed, leaveId } = dialogData
    setLoading(true)

    try {
      await callFunction('saveLeaves', {
        ...filed,
        id: leaveId,
        note: text,
        void: true,
      })
    } catch (ex) {
      console.log(ex)
    }

    setLoading(false)
  }

  function handleClose() {
    setDialogData(null)
  }

  return (
    <Grid container spacing={1}>
      {loading && <Backdrop open>
        <CircularProgress color="inherit" />
      </Backdrop>}
      {dialogData && <ActionDialog
        title={formatMessage({ id: `button.${dialogData.action}` })}
        handleClose={handleClose}
        handleExecute={handleExecute}
        textFieldLabel={formatMessage({ id: 'leaves.detail.voidReason' })}
        action={dialogData.action}
      />}
      <Grid item xs={12} sm={12} md={12}>
        <div style={{ paddingBottom: '60px', paddingTop: '20px' }}>
          <EnhancedTable
            defaultOrder="desc"
            defaultOrderField="date"
            headerCells={headerCells}
            rowCells={rowCells}
            tableData={leaves.map(r => formatData(r))}
            getActionIcons={leave =>
              <>
                {(!leave.void || (leave.void && leave.status === 'reject')) && <span>
                  <Tooltip title={formatMessage({ id: 'button.edit' })}>
                    <IconButton
                      onClick={() => navigate(`/leaveOvertimeWork/leaves/edit/${leave.createdBy}/${leave.id}`)}
                      size="large">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={formatMessage({ id: 'button.void' })}>
                    <IconButton
                      onClick={() => setDialogData({ action: 'void', title: '', filed: leave, leaveId: leave.id })}
                      size="large">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </span>}
              </>
            }
            getExpandContent={leave =>
              <LeavesView leave={leave} currentUser={currentUser} type='owner' />
            }
          />
        </div>
      </Grid>
    </Grid>
  );
};

export default LeaveList;
